import React from 'react';
import { Box, BoxProps } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { SxProps, Theme } from '@mui/material/styles';

interface ResponsiveBoxProps extends BoxProps {
  responsiveHeight?: {
    xs?: number | string;
    sm?: number | string;
    md?: number | string;
    lg?: number | string;
    xl?: number | string;
  };
  responsiveDirection?: {
    xs?: string;
    sm?: string;
    md?: string;
    lg?: string;
    xl?: string;
  };
}

/**
 * ResponsiveBox is an enhanced version of Material UI's Box component
 * with built-in responsive behavior and optimizations for mobile devices
 */
const ResponsiveBox: React.FC<ResponsiveBoxProps> = ({ 
  children, 
  sx, 
  responsiveHeight,
  responsiveDirection,
  ...props 
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Get padding from sx prop if it exists
  const extractPadding = (sxProp?: SxProps<Theme>) => {
    if (!sxProp) return undefined;
    
    // If sx is an array, try to find an object with a 'p' property
    if (Array.isArray(sxProp)) {
      for (const item of sxProp) {
        if (typeof item === 'object' && item !== null && 'p' in item) {
          return item.p;
        }
      }
      return undefined;
    }
    
    // If sx is an object, check if it has a 'p' property
    if (typeof sxProp === 'object' && sxProp !== null && 'p' in sxProp) {
      return sxProp.p;
    }
    
    return undefined;
  };
  
  // Default responsive styles
  const responsiveStyles: SxProps<Theme> = {
    // Add appropriate padding on mobile
    p: isMobile ? 2 : extractPadding(sx) || 3,
    
    // Ensure proper overflow handling
    overflow: 'hidden',
    
    // Apply height if provided
    ...(responsiveHeight && {
      height: responsiveHeight
    }),
    
    // Apply direction if provided
    ...(responsiveDirection && {
      flexDirection: responsiveDirection
    }),
    
    // Apply other styles from the sx prop
    ...(sx || {})
  };
  
  return (
    <Box
      sx={responsiveStyles}
      {...props}
    >
      {children}
    </Box>
  );
};

export default ResponsiveBox; 